import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, LayoutDashboard, CalendarDays, Armchair, Sparkles, Ticket,
  Mail, Image as ImageIcon, FileText, Plus, Trash2, Loader2, DollarSign, Users, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "events", label: "Events", icon: CalendarDays },
  { id: "reservations", label: "Reservations", icon: Armchair },
  { id: "inquiries", label: "Private Events", icon: Sparkles },
  { id: "tickets", label: "Ticket Sales", icon: Ticket },
  { id: "payments", label: "Deposits", icon: DollarSign },
  { id: "subscribers", label: "Subscribers", icon: Mail },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "content", label: "Blog & Reviews", icon: FileText },
];

const field = "w-full bg-neutral-100 border border-black/12 rounded-lg px-3 py-2 text-neutral-900 text-sm placeholder:text-neutral-300 outline-none focus:border-neutral-900";
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function EventForm({ onSaved, onClose }) {
  const [f, setF] = useState({ title: "", tagline: "", description: "", flyer_image: "", date: "", start_time: "9:00 PM", dj: "", cover_charge: "", featured: false, status: "upcoming" });
  const [tiers, setTiers] = useState([{ name: "General Admission", price: 20, capacity: 150, sold: 0, description: "" }]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.title || !f.date) { toast.error("Title and date are required."); return; }
    setSaving(true);
    try {
      await api.post("/admin/events", { ...f, slug: slugify(f.title), tiers: tiers.map((t) => ({ ...t, price: Number(t.price), capacity: Number(t.capacity), sold: Number(t.sold || 0) })) });
      toast.success("Event created.");
      onSaved(); onClose();
    } catch (e) {
      toast.error("Could not save event (slug may exist).");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white border border-black/10 rounded-2xl p-7 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-serif text-2xl text-neutral-900">New Event</h3>
          <button onClick={onClose} className="text-neutral-400"><X size={20} /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><label className="overline block mb-1.5">Title</label><input data-testid="ef-title" value={f.title} onChange={(e) => set("title", e.target.value)} className={field} /></div>
          <div className="md:col-span-2"><label className="overline block mb-1.5">Tagline</label><input value={f.tagline} onChange={(e) => set("tagline", e.target.value)} className={field} /></div>
          <div className="md:col-span-2"><label className="overline block mb-1.5">Description</label><textarea rows="3" value={f.description} onChange={(e) => set("description", e.target.value)} className={field} /></div>
          <div className="md:col-span-2"><label className="overline block mb-1.5">Flyer Image URL</label><input data-testid="ef-image" value={f.flyer_image} onChange={(e) => set("flyer_image", e.target.value)} className={field} placeholder="https://…" /></div>
          <div><label className="overline block mb-1.5">Date</label><input data-testid="ef-date" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className={field} /></div>
          <div><label className="overline block mb-1.5">Start Time</label><input value={f.start_time} onChange={(e) => set("start_time", e.target.value)} className={field} /></div>
          <div><label className="overline block mb-1.5">DJ / Host</label><input value={f.dj} onChange={(e) => set("dj", e.target.value)} className={field} /></div>
          <div><label className="overline block mb-1.5">Cover Charge</label><input value={f.cover_charge} onChange={(e) => set("cover_charge", e.target.value)} className={field} /></div>
          <div><label className="overline block mb-1.5">Status</label>
            <select value={f.status} onChange={(e) => set("status", e.target.value)} className={field}><option value="upcoming" className="bg-white">Upcoming</option><option value="past" className="bg-white">Past</option></select>
          </div>
          <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[#D4AF37]" /><span className="text-neutral-600 text-sm">Featured</span></label>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-3"><label className="overline">Ticket Tiers</label>
            <button onClick={() => setTiers((t) => [...t, { name: "", price: 0, capacity: 100, sold: 0, description: "" }])} className="text-neutral-900 text-sm inline-flex items-center gap-1"><Plus size={14} /> Add Tier</button>
          </div>
          <div className="space-y-2">
            {tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-[1fr_80px_80px_28px] gap-2 items-center">
                <input placeholder="Tier name" value={t.name} onChange={(e) => setTiers((p) => p.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className={field} />
                <input type="number" placeholder="$" value={t.price} onChange={(e) => setTiers((p) => p.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} className={field} />
                <input type="number" placeholder="Cap" value={t.capacity} onChange={(e) => setTiers((p) => p.map((x, j) => j === i ? { ...x, capacity: e.target.value } : x))} className={field} />
                <button onClick={() => setTiers((p) => p.filter((_, j) => j !== i))} className="text-neonred"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>

        <button data-testid="ef-save" onClick={save} disabled={saving} className="btn-gold w-full justify-center mt-7">{saving ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Create Event</button>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="card-lux p-6">
      <Icon size={22} className={accent ? "text-neutral-900" : "text-neutral-400"} />
      <div className="font-serif text-4xl text-neutral-900 mt-4">{value}</div>
      <div className="text-neutral-400 text-xs uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user, checked, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({});
  const [showEventForm, setShowEventForm] = useState(false);

  useEffect(() => { if (checked && user === false) navigate("/admin/login"); }, [checked, user, navigate]);

  const loadStats = useCallback(() => { api.get("/admin/analytics").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const load = useCallback((key, url) => { api.get(url).then((r) => setData((d) => ({ ...d, [key]: r.data }))).catch(() => {}); }, []);

  useEffect(() => {
    if (!user || user === false) return;
    loadStats();
    load("events", "/admin/events");
    load("reservations", "/admin/reservations");
    load("inquiries", "/admin/inquiries");
    load("tickets", "/admin/tickets");
    load("payments", "/admin/payments");
    load("subscribers", "/admin/subscribers");
    load("gallery", "/admin/gallery");
    load("blog", "/admin/blog");
  }, [user, load, loadStats]);

  if (!checked || user === null) return <div className="min-h-screen flex items-center justify-center bg-transparent text-neutral-400">Loading…</div>;
  if (user === false) return null;

  const delItem = async (url, key, listUrl) => {
    try { await api.delete(url); toast.success("Deleted."); load(key, listUrl); loadStats(); } catch { toast.error("Could not delete."); }
  };
  const setResStatus = async (id, status) => { await api.patch(`/admin/reservations/${id}`, { status }); load("reservations", "/admin/reservations"); loadStats(); };
  const setInqStatus = async (id, status) => { await api.patch(`/admin/inquiries/${id}`, { status }); load("inquiries", "/admin/inquiries"); loadStats(); };

  const addGallery = async () => {
    const url = prompt("Image URL:"); if (!url) return;
    const category = prompt("Category (Hookah/Cocktails/Food/Nightlife):", "Nightlife") || "Nightlife";
    await api.post("/admin/gallery", { image_url: url, category, caption: "" });
    toast.success("Added."); load("gallery", "/admin/gallery");
  };

  return (
    <div className="min-h-screen bg-transparent grain flex">
      <aside className="w-64 border-r border-black/10 bg-white p-6 hidden lg:flex flex-col relative z-[2]">
        <div className="font-serif text-2xl tracking-[0.2em] text-accent mb-1">CHI-CHA</div>
        <p className="text-neutral-400 text-[10px] uppercase tracking-[0.3em] mb-8">Dashboard</p>
        <nav className="flex-1 space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${tab === t.id ? "bg-neutral-900/10 text-neutral-900" : "text-neutral-500 hover:text-neutral-900"}`}>
                <Icon size={17} /> {t.label}
              </button>
            );
          })}
        </nav>
        <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-3 px-3 py-2.5 text-neutral-400 hover:text-neonred text-sm mt-4"><LogOut size={17} /> Logout</button>
      </aside>

      <main className="flex-1 p-6 md:p-10 relative z-[2] overflow-x-hidden">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h1 className="font-serif text-4xl text-neutral-900 capitalize">{TABS.find((t) => t.id === tab)?.label}</h1>
          <div className="flex gap-2 lg:hidden overflow-x-auto">
            {TABS.map((t) => <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${tab === t.id ? "bg-neutral-900 text-white" : "glass text-neutral-500"}`}>{t.label}</button>)}
          </div>
        </div>

        {tab === "overview" && stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <StatCard icon={DollarSign} label="Ticket Revenue" value={`$${stats.revenue}`} accent />
            <StatCard icon={Ticket} label="Tickets Sold" value={stats.tickets_sold} accent />
            <StatCard icon={CalendarDays} label="Upcoming Events" value={stats.upcoming_events} />
            <StatCard icon={Armchair} label="Reservations" value={stats.reservations} />
            <StatCard icon={Armchair} label="Pending Reservations" value={stats.pending_reservations} accent />
            <StatCard icon={Sparkles} label="Event Inquiries" value={stats.inquiries} />
            <StatCard icon={Sparkles} label="New Inquiries" value={stats.new_inquiries} accent />
            <StatCard icon={Users} label="Subscribers" value={stats.subscribers} />
          </div>
        )}

        {tab === "events" && (
          <div>
            <button data-testid="new-event-btn" onClick={() => setShowEventForm(true)} className="btn-gold mb-6"><Plus size={16} /> New Event</button>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {(data.events || []).map((ev) => (
                <div key={ev.id} className="card-lux p-5">
                  <div className="flex gap-4">
                    <img src={ev.flyer_image} alt="" className="w-16 h-16 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif text-lg text-neutral-900 truncate">{ev.title}</h4>
                      <p className="text-neutral-400 text-xs">{ev.date} · {ev.status}</p>
                      <p className="text-neutral-900 text-xs mt-1">{(ev.tiers || []).reduce((a, t) => a + (t.sold || 0), 0)} sold</p>
                    </div>
                    <button data-testid={`del-event-${ev.slug}`} onClick={() => delItem(`/admin/events/${ev.id}`, "events", "/admin/events")} className="text-neonred/70 hover:text-neonred"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "reservations" && (
          <div className="card-lux overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-900 text-neutral-200 text-xs uppercase tracking-wider"><tr><th className="text-left p-4">Guest</th><th className="text-left p-4">Date/Time</th><th className="text-left p-4">Details</th><th className="text-left p-4">Status</th></tr></thead>
              <tbody>
                {(data.reservations || []).map((r) => (
                  <tr key={r.id} className="border-t border-black/8">
                    <td className="p-4"><div className="text-neutral-900">{r.name}</div><div className="text-neutral-400 text-xs">{r.email}</div><div className="text-neutral-400 text-xs">{r.phone}</div></td>
                    <td className="p-4 text-neutral-600">{r.date}<br/>{r.time}</td>
                    <td className="p-4 text-neutral-600">
                      {r.guests} guests · {r.seating}{r.bottle_service ? " · Bottle Service" : ""}
                      {r.special_requests ? <div className="text-neutral-400 text-xs mt-1 italic">“{r.special_requests}”</div> : null}
                    </td>
                    <td className="p-4">
                      <select data-testid={`res-status-${r.id}`} value={r.status} onChange={(e) => setResStatus(r.id, e.target.value)} className="bg-neutral-100 border border-black/12 rounded-lg px-2 py-1.5 text-xs text-neutral-900">
                        <option value="pending" className="bg-white">Pending</option><option value="confirmed" className="bg-white">Confirmed</option><option value="cancelled" className="bg-white">Cancelled</option>
                      </select>
                    </td>
                  </tr>
                ))}
                {(data.reservations || []).length === 0 && <tr><td colSpan="4" className="p-8 text-center text-neutral-400">No reservations yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "inquiries" && (
          <div className="grid md:grid-cols-2 gap-4">
            {(data.inquiries || []).map((i) => (
              <div key={i.id} className="card-lux p-5">
                <div className="flex justify-between items-start"><div><h4 className="font-serif text-lg text-neutral-900">{i.name}</h4><p className="text-neutral-900 text-xs">{i.event_type} · {i.guests} guests</p></div>
                  <select value={i.status} onChange={(e) => setInqStatus(i.id, e.target.value)} className="bg-neutral-100 border border-black/12 rounded-lg px-2 py-1 text-xs text-neutral-900"><option value="new" className="bg-white">New</option><option value="contacted" className="bg-white">Contacted</option><option value="booked" className="bg-white">Booked</option><option value="closed" className="bg-white">Closed</option></select>
                </div>
                <p className="text-neutral-400 text-xs mt-2">{i.email} · {i.phone} · {i.date || "flexible"}</p>
                {i.message && <p className="text-neutral-500 text-sm mt-3 border-t border-black/8 pt-3">{i.message}</p>}
              </div>
            ))}
            {(data.inquiries || []).length === 0 && <p className="text-neutral-400">No inquiries yet.</p>}
          </div>
        )}

        {tab === "tickets" && (
          <div className="card-lux overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-900 text-neutral-200 text-xs uppercase tracking-wider"><tr><th className="text-left p-4">Attendee</th><th className="text-left p-4">Tier</th><th className="text-left p-4">Qty</th><th className="text-left p-4">Status</th></tr></thead>
              <tbody>
                {(data.tickets || []).map((t) => (
                  <tr key={t.id} className="border-t border-black/8"><td className="p-4"><div className="text-neutral-900">{t.name}</div><div className="text-neutral-400 text-xs">{t.email}</div></td><td className="p-4 text-neutral-600">{t.tier_name}</td><td className="p-4 text-neutral-600">{t.quantity}</td><td className="p-4"><span className="text-neutral-900 text-xs uppercase">{t.status}</span></td></tr>
                ))}
                {(data.tickets || []).length === 0 && <tr><td colSpan="4" className="p-8 text-center text-neutral-400">No ticket sales yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "payments" && (
          <div className="card-lux overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-900 text-neutral-200 text-xs uppercase tracking-wider"><tr><th className="text-left p-4">Guest</th><th className="text-left p-4">Event / Tier</th><th className="text-left p-4">Amount</th><th className="text-left p-4">Status</th><th className="text-left p-4">When</th></tr></thead>
              <tbody>
                {(data.payments || []).map((p) => (
                  <tr key={p.id} className="border-t border-black/8" data-testid={`payment-row-${p.id}`}>
                    <td className="p-4"><div className="text-neutral-900">{p.name}</div><div className="text-neutral-400 text-xs">{p.email}</div></td>
                    <td className="p-4 text-neutral-600">{p.event_title}<div className="text-neutral-400 text-xs">{p.tier_name} × {p.quantity}</div></td>
                    <td className="p-4"><div className="text-neutral-900 font-semibold">${Number(p.amount || 0).toFixed(2)}</div><div className="text-neutral-400 text-xs">${Number(p.subtotal || 0).toFixed(2)} + ${Number(p.processing_fee || 0).toFixed(2)} fee</div></td>
                    <td className="p-4"><span className={`text-xs font-semibold uppercase ${p.payment_status === "paid" ? "text-green-600" : "text-amber-600"}`}>{p.payment_status === "paid" ? "Paid" : "Pending"}</span></td>
                    <td className="p-4 text-neutral-400 text-xs">{p.created_at ? new Date(p.created_at).toLocaleString("en-US", { timeZone: "America/New_York" }) : "—"}</td>
                  </tr>
                ))}
                {(data.payments || []).length === 0 && <tr><td colSpan="5" className="p-8 text-center text-neutral-400">No deposits yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "subscribers" && (
          <div className="card-lux p-6">
            <p className="text-neutral-400 text-sm mb-4">{(data.subscribers || []).length} subscribers</p>
            <div className="flex flex-wrap gap-2">
              {(data.subscribers || []).map((s) => <span key={s.id} className="glass rounded-full px-3 py-1.5 text-sm text-neutral-600">{s.email}</span>)}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div>
            <button data-testid="add-gallery-btn" onClick={addGallery} className="btn-gold mb-6"><Plus size={16} /> Add Image</button>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(data.gallery || []).map((g) => (
                <div key={g.id} className="relative group rounded-xl overflow-hidden aspect-square">
                  <img src={g.image_url} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => delItem(`/admin/gallery/${g.id}`, "gallery", "/admin/gallery")} className="absolute top-2 right-2 bg-black/70 text-neonred p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "content" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="card-lux p-6">
              <h3 className="font-serif text-2xl text-neutral-900 mb-4">Blog Posts ({(data.blog || []).length})</h3>
              <div className="space-y-2">
                {(data.blog || []).map((b) => (
                  <div key={b.id} className="flex items-center justify-between border-b border-black/8 pb-2"><span className="text-neutral-600 text-sm truncate">{b.title}</span><span className="text-neutral-300 text-xs">{b.published ? "Live" : "Draft"}</span></div>
                ))}
              </div>
              <p className="text-neutral-400 text-xs mt-4">Blog editing via API. Sample posts are pre-loaded and SEO-optimized.</p>
            </div>
            <div className="card-lux p-6">
              <h3 className="font-serif text-2xl text-neutral-900 mb-4">Reviews</h3>
              <p className="text-neutral-400 text-sm">Testimonials are managed via the API and displayed in the homepage carousel.</p>
            </div>
          </div>
        )}
      </main>

      {showEventForm && <EventForm onSaved={() => { load("events", "/admin/events"); loadStats(); }} onClose={() => setShowEventForm(false)} />}
    </div>
  );
}
